import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { apiErrorMessage } from '@/shared/utils/api-error'
import { FIELD_LABELS, type useChartIdSearch, type ChartIdSearchRequest } from '../hooks/use-chart-id-search'
import { chartRoute, plural, useChartRow } from '../hooks/use-chart-row'
import type { IdHit } from '../utils/id-hit'

/**
 * Chart-identifier search results — the search panel's bulk view: count line,
 * expandable unmatched list, compact rows and a pager, all inside the panel.
 * Values render exactly as the API returns them (masked without PHI access).
 */
export function ChartIdResults({
  request,
  search,
  onPage,
  onOpen,
}: {
  request: ChartIdSearchRequest
  search: ReturnType<typeof useChartIdSearch>
  onPage: (page: number) => void
  /** A row was opened in this tab (the panel closes). */
  onOpen?: () => void
}) {
  const row = useChartRow()
  const [showUnmatched, setShowUnmatched] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  const cond = request.conditions[0]
  const noun = FIELD_LABELS[cond.field]
  // Each row's matched identifier is marked against the values it was searched with.
  const hit: IdHit = cond
  // The previous request's rows/counts would read as this request's: treat them
  // as not-yet-loaded so the skeleton shows instead.
  const data = search.isPlaceholderData ? undefined : search.data
  const unmatched = data?.summary.flatMap((s) => s.unmatched) ?? []
  const from = data ? (data.page - 1) * data.page_size + 1 : 0
  const to = data ? Math.min(data.total, data.page * data.page_size) : 0

  function goTo(page: number) {
    onPage(page)
    listRef.current?.scrollIntoView?.({ block: 'nearest' })
  }

  // Plain click opens here (and closes the panel); Cmd/Ctrl/Shift/middle click
  // keep the browser's own new-tab behavior.
  const onLinkClick = (e: React.MouseEvent) => {
    if (!e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) onOpen?.()
  }

  // Up/Down move between the row links (one list, like the single-mode results).
  function onRowsKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const links = [...e.currentTarget.querySelectorAll<HTMLAnchorElement>('a')]
    const i = links.indexOf(document.activeElement as HTMLAnchorElement)
    if (i < 0) return
    e.preventDefault()
    links[Math.min(Math.max(i + (e.key === 'ArrowDown' ? 1 : -1), 0), links.length - 1)]?.focus()
  }

  return (
    <section className="cid cid--rows" aria-label="Chart ID results">
      <div className="cid-bar">
        {data ? (
          <strong>{plural(data.total, 'chart')} found</strong>
        ) : (
          <span>Searching {plural(cond.values.length, noun)}…</span>
        )}
        {data && search.isFetching && <span className="cid-refresh">Refreshing…</span>}
        {data && unmatched.length > 0 && (
          <>
            <span className="cid-sep" aria-hidden="true">·</span>
            <button
              type="button"
              className="cid-link"
              aria-expanded={showUnmatched}
              onClick={() => setShowUnmatched((v) => !v)}
            >
              {plural(unmatched.length, 'identifier')} unmatched <span aria-hidden="true">{showUnmatched ? '▾' : '▸'}</span>
            </button>
          </>
        )}
      </div>

      {data && showUnmatched && unmatched.length > 0 && (
        <div className="cid-unmatched">
          <span>No chart within your access and filters:</span> <code>{unmatched.join(', ')}</code>
        </div>
      )}

      {search.isError && (
        <p className="cid-error" role="alert">
          {apiErrorMessage(search.error, 'Search failed.')}{' '}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void search.refetch()}>Retry</button>
        </p>
      )}

      {data?.results.length === 0 && (
        <p className="cid-empty">
          No charts match {cond.values.length === 1 ? 'this' : `these ${cond.values.length}`} {cond.values.length === 1 ? noun : `${noun}s`} within your access and filters.
        </p>
      )}

      {!data && search.isFetching && (
        <div className="sp-skeleton" aria-hidden="true"><span /><span /><span /></div>
      )}

      {data && data.results.length > 0 && (
        <div ref={listRef} className="cid-rows" aria-busy={search.isFetching} onKeyDown={onRowsKey}>
          {data.results.map((r) => {
            const { content, label } = row(r, '', hit)
            return (
              <Link key={r.id} to={chartRoute(r)} className="sp-row sp-row--chart" aria-label={label} onClick={onLinkClick}>
                {content}
              </Link>
            )
          })}
        </div>
      )}

      {data && data.total > data.page_size && (
        <nav className="cid-pager" aria-label="Pages">
          <span>{from}–{to} of {data.total}</span>
          <span className="cid-pager-btns">
            <button type="button" className="pgn-btn" aria-label="Previous page" disabled={data.page <= 1 || search.isFetching} onClick={() => goTo(data.page - 1)}>
              <ChevronLeft size={14} />
            </button>
            <button type="button" className="pgn-btn" aria-label="Next page" disabled={to >= data.total || search.isFetching} onClick={() => goTo(data.page + 1)}>
              <ChevronRight size={14} />
            </button>
          </span>
        </nav>
      )}
    </section>
  )
}
